import { MutableRefObject, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { Grid } from '@material-ui/core';
import { ButtonControlGroup } from '../../../helpers/ButtonControlGroup';
import { UserProfilePrefs } from '../../../../../modules/Helpers/UserProfilePrefs';
import RowTextFieldWithMetrics from '../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics';
import RowSelectWithMetrics from '../../../../../modules/Farms/Intervention/RowSelectWithMetrics';
import { unitLong } from '../../../../utils/unit-utils';
import { DialogContainer } from '../../CommonDataParts/DialogContainer';
import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import { DairyInterventionFormDialogProps } from '../CommonDataParts/InterventionTypes';
import { MasterDataOption, Maybe } from '../../../../../../graphql/types';
import { FormType } from '../../common';
import { DairyIntervention, DairyManure } from '../../../../models/Intervention';

interface DInterventionManureFormDialogProps
  extends DairyInterventionFormDialogProps {
  manureManagementSystems: Maybe<Array<MasterDataOption>>;
  averageAnimalsPresentRef: MutableRefObject<number | string | null>;
}

const DInterventionManureFormDialog = ({
  baseline,
  formVisible,
  stageIndex = 0,
  formType = FormType.Add,
  handleCancel,
  manureManagementSystems,
  averageAnimalsPresentRef,
}: DInterventionManureFormDialogProps) => {

  const intl = useIntl();
  
  // TODO Fix this after coverting UserProfilePrefs to TS
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const userProfile = UserProfilePrefs.getInstance();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const userUOM = userProfile.getUserUnitPrefs();
  const currResetValue = useRef<DairyManure>();
  
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const precision = userProfile.getUnitBarnOutputMassPrecision() as number;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const barnOutputMassUnit = userUOM?.unitBarnOutputMass
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
    unitLong(userUOM.unitBarnOutputMass)
    : 'kg';

  const formTitle = intl.formatMessage(
    {
      id: 'SUSTELL.PROCESS.DIALOG.STAGE.MANURE.TITLE',
    },
    {
      br: ' ', // in dialog we don't want line break, so just space
    }
  );

  const fieldItemPrefix = `stages.${stageIndex}.stageData.manure`;
  // const { control, getValues, reset, setValue, trigger } = useFormContext();
  const fc = useFormContext();

  useEffect(() => {
    if (formVisible) {
      currResetValue.current = { ...(fc.getValues(fieldItemPrefix) as DairyManure) }; // must 'spread' otherwise just reference
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible]);

  const handleResetClick = () => {
    if (currResetValue.current) {
      const resetObject = { ...fc.getValues() } as DairyIntervention;
      const stageData = resetObject?.stages?.[stageIndex]?.stageData;
      if (stageData?.manure) {
        stageData.manure = {
          ...currResetValue.current,
        };
        fc.reset(resetObject, {
          errors: true,
        });
      }
    }
    handleCancel();
  };

  // TODO REMOVE THE name PARAM IF REALY NOT NEEDED AFTER CONVERTING ALL TO TS
  const avgAnimalsChanged = (name: string , ref:  MutableRefObject<number | string>, value: number | string) => {
    
    if (!value) {
      return;
    }
    // eslint-disable-next-line no-param-reassign
    ref.current = value;
    if (fc.getValues(`stages.${stageIndex}.stageData.emissions.is3NOPSelected`)) {
      fc.setValue(`stages.${stageIndex}.stageData.emissions.supplement3NOP.calculatedResult_recalculate_error`,
                 intl.formatMessage({id: "BOVAER.ERROR.CALCULATE_AGAIN" }));
      (async () => fc.trigger(`stages.${stageIndex}.stageData.emissions.supplement3NOP`)) ().catch(() => console.log('err'));;
    }
  };

  const manure = baseline?.stages?.[stageIndex]?.stageData?.manure;

  return (
    <DialogContainer
      formVisible={formVisible}
      variant="wide"
      handleClose={handleResetClick}
      iconCode="manureIcon"
      datasetType="intervention"
      formTitle={formTitle}
    >
      <Grid container direction="row" spacing={2} justifyContent="space-between">
        <Grid item container direction="column" xs={6} spacing={2}>
          <Grid item>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix  }.averageAnimalsPresent`}
              label={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.AVERAGE_ANIMALS_PRESENT',
              })}
              type="number"
              tooltip={intl.formatMessage({
                id: 'INTERVENTION.FORM.BARN.AVERAGE_ANIMALS_PRESENT.TOOLTIP',
              })}
              metricUnit="number"
              margin="none"
              updateRef={avgAnimalsChanged}
              inputRef={averageAnimalsPresentRef}
              baseline={manure?.averageAnimalsPresent}
              disabled={formType === 'view'}
            />
          </Grid>
          <Grid item>
            <RowSelectWithMetrics
              name={`${fieldItemPrefix  }.manureManagementSystem`}
              label={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.MANURE_MANAGEMENT_SYSTEM',
              })}
              control={fc.control}
              variant="outlined"
              disabled={formType === 'view'}
              items={manureManagementSystems}
              baseline={manure?.manureManagementSystem}
              margin="none"
              tooltip={
                <div>
                  {intl.formatMessage(
                    {
                      id: 'BASELINE.FORM.BARN.DAIRY.MANURE_MANAGEMENT_SYSTEM.TOOLTIP',
                    },
                    { br: <br /> }
                  )}
                </div>
              }
            />
          </Grid>
          <Grid item style={{ marginTop: '1.75rem' }}>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix  }.solidManureStorage`}
              label={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.SOLID_MANURE',
              })}
              type="number"
              tooltip={intl.formatMessage({
                id: 'INTERVENTION.FORM.BARN.SOLID_MANURE.TOOLTIP',
              })}
              metricUnit="percentage"
              baseline={manure?.solidManureStorage}
              disabled={formType === 'view'}
              minAllowedValue={0}
              maxAllowedValue={100}
            />
          </Grid>
        </Grid>
        <Grid item container direction="column" xs={6} spacing={2}>
          <Grid item>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix  }.slurryManureStorage`}
              label={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.SLURRY_MANURE',
              })}
              type="number"
              tooltip={intl.formatMessage({
                id: 'INTERVENTION.FORM.BARN.SLURRY_MANURE.TOOLTIP',
              })}
              metricUnit="percentage"
              margin="none"
              baseline={manure?.slurryManureStorage}
              disabled={formType === 'view'}
              minAllowedValue={0}
              maxAllowedValue={100}
            />
          </Grid>
          <Grid item>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix  }.strawForBedding`}
              label={intl.formatMessage({
                id: 'BASELINE.FORM.BARN.STRAW_FOR_BEDDING',
              })}
              type="number"
              tooltip={intl.formatMessage({
                id: 'INTERVENTION.FORM.BARN.STRAW_FOR_BEDDING.TOOLTIP',
              })}
              metricUnit={intl.formatMessage(
                { id: 'ADDORNMENT.UNITS.WITH_PER_ANIMAL_SUFIX' },
                { unit: barnOutputMassUnit }
              )}
              precision={precision}
              margin="none"
              baseline={manure?.strawForBedding}
              disabled={formType === 'view'}
              minAllowedValue={0}
              maxAllowedValue={100}
            />
          </Grid>
          <Grid item>
            <RowTextFieldWithMetrics
              name={`${fieldItemPrefix  }.sawDust`}
              label={intl.formatMessage({ id: 'BASELINE.FORM.BARN.SAW_DUST' })}
              type="number"
              tooltip={intl.formatMessage({
                id: 'INTERVENTION.FORM.BARN.SAW_DUST.TOOLTIP',
              })}
              metricUnit={intl.formatMessage(
                { id: 'ADDORNMENT.UNITS.WITH_PER_ANIMAL_SUFIX' },
                { unit: barnOutputMassUnit }
              )}
              precision={precision}
              margin="none"
              baseline={manure?.sawDust}
              disabled={formType === 'view'}
              minAllowedValue={0}
              maxAllowedValue={100}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ButtonControlGroup
            cancelHandler={handleResetClick}
            saveHandler={handleCancel}
            saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
          />
        </Grid>
      </Grid>
    </DialogContainer>
  );
};

export default DInterventionManureFormDialog;